import { FC, useCallback } from "react";
import { SettingPageWrapper } from "@connectedComponents";
import { NavigationPathsEnum } from "@utilities/enums";
import { Card, DataTable, Icon, Pagination, GenericActionsBar } from "@components";
import { useControllers } from "./useControllers";
import { CollaboratorCard } from "./components";
import { User } from "@utilities/types";
import { UserShopRoleEnum } from "@utilities/types";
import { CollaboratorModal } from "./components/CollaboratorModal";
import { formatDate } from "@utilities/functions";

export const CollaboratorsSettings: FC = () => {
    const {
        user, currentPage, totalPages,
        paginatedCollaborators,
        loadingGetCollaborators, createAccountError,
        selectedCollaboratorFilter, setSelectedCollaboratorFilter,
        displayCollabModal, onToggleCollabModal,
        loadingCollabModal, onAddNewCollaborator,
        setSearchCollaborator, onChangePage,
    } = useControllers();

    const renderActions = useCallback((collaborator: User) => (
        <div className="flex items-center justify-start gap-2">
            <button
                className="btn btn-ghost btn-sm btn-circle"
                title="Modifier"
                disabled={user?._id === collaborator._id}
            >
                <Icon iconName="UserRoundPen" size={16} />
            </button>
            <button
                onClick={() => { }}
                className="btn btn-ghost btn-sm btn-circle text-error"
                title="Supprimer"
            >
                <Icon iconName="UserX" size={16} />
            </button>
        </div>
    ), [user]);

    const renderBadge = useCallback((collaborator: User) => {
        const roleBadge = (role: UserShopRoleEnum) => {
            switch (role) {
                case UserShopRoleEnum.admin:
                    return "badge badge-primary";
                case UserShopRoleEnum.subAdmin:
                    return "badge badge-secondary";
                case UserShopRoleEnum.seller:
                    return "badge badge-success";
                default:
                    return "badge badge-ghost";
            }
        };

        return (
            <div className={`${roleBadge(collaborator.role)} p-2`}>
                {collaborator.role}
            </div>
        );
    }, []);

    const renderMobileCollaboratorsView = useCallback(()=> (
        <div className="md:hidden space-y-4 mt-4">
            {
                paginatedCollaborators.length > 0 ? (
                    paginatedCollaborators.map((collaborator) => (
                        <CollaboratorCard
                            key={collaborator._id}
                            collaborator={collaborator}
                            renderBadge={renderBadge}
                            renderDropdownMenu={renderActions}
                            formatDate={formatDate}
                        />
                    ))
                ) : (
                    <div className="text-center py-8">
                        Aucun résultat trouvé
                    </div>
                )
            }
        </div>
    ), [paginatedCollaborators, renderActions, renderBadge]);

    const renderCollaboratorRow = useCallback((collaborator: User) => {
        return (
            <tr key={collaborator._id}>
                <td className="font-medium">{collaborator.firstname} {collaborator.lastname}</td>
                <td>{collaborator.email}</td>
                <td>{renderBadge(collaborator)}</td>
                <td>{formatDate(collaborator.createAt)}</td>
                <td className="text-center">{renderActions(collaborator)}</td>
            </tr>
        )
    }, [renderActions, renderBadge]);

    const renderDesktopCollaboratorsView = useCallback(() => (
        <div className="hidden md:block">
            {
                paginatedCollaborators.length > 0 ? (
                    <DataTable
                        items={paginatedCollaborators}
                        headers={["Nom complet", "Email", "Rôle", "Date de création", "Actions"]}
                        renderRow={renderCollaboratorRow}
                    />
                ) : (
                    <div className="text-center py-8">
                        Aucun résultat trouvé
                    </div>
                )
            }
        </div>
    ), [paginatedCollaborators, renderCollaboratorRow]);

    return (
        <SettingPageWrapper activeTab={NavigationPathsEnum.SETTINGS_COLLABORATORS}>
            <Card
                title="Gestion des collaborateurs"
                customClassName="bg-none md:bg-white"
                actions={
                    <GenericActionsBar
                        searchPlaceholder="Rechercher un collaborateur..."
                        handleSearch={setSearchCollaborator}
                        selectedFilterValue={selectedCollaboratorFilter}
                        handleFilterChange={setSelectedCollaboratorFilter}
                        filterOptions={[
                            { value: UserShopRoleEnum.seller, label: UserShopRoleEnum.seller },
                            { value: UserShopRoleEnum.subAdmin, label: UserShopRoleEnum.subAdmin },
                            { value: UserShopRoleEnum.admin, label: UserShopRoleEnum.admin },
                        ]}
                        customActions={[
                            <button
                                className="btn btn-ghost btn-circle"
                                onClick={onToggleCollabModal}
                            >
                                <Icon iconName="UserRoundPlus" size={20} />
                            </button> 
                        ]}
                    />
                }
            >
                {
                    loadingGetCollaborators ? (
                        <div className="flex justify-center items-center py-8">
                            <span className="loading loading-spinner loading-lg"></span>
                        </div>
                    ) : (
                        <>
                            {renderMobileCollaboratorsView()}

                            {renderDesktopCollaboratorsView()}

                            <div className="block">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={onChangePage}
                                />
                            </div>
                        </>
                    )
                }
            </Card>

            <CollaboratorModal 
                isOpen={displayCollabModal} 
                isLoading={loadingCollabModal} 
                error={createAccountError}
                onClose={onToggleCollabModal} 
                onButtonClick={(props)=> onAddNewCollaborator(props as any)}                
            />
        </SettingPageWrapper >
    );
};
